import { fn } from '@ember/helper';
import { action, get, set } from '@ember/object';
import { trackedSet } from '@ember/reactive/collections';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { getCssVariableValue } from 'frontend-utils/utils/css-variable';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralTooltipsTooltipRatesCalendar from 'lumen/components/general/tooltips/tooltip-rates-calendar';
import {
  collectMeanData,
  getWeekendPlotBands,
} from 'lumen/utils/charts/chart-utils';
import {
  calculateTickPositions,
  calculateYaxisMax,
  connectRateDataPoint,
  connectUndefinedRateDataPoint,
  getSeriesConfiguration,
} from 'lumen/utils/charts/rates-chart-utils';
import { addZone } from 'lumen/utils/charts/zones';
import { SHORT_TERM_RENTAL_RATE_COLOR } from 'lumen/utils/constants/chart-color-constants';
import { HIGHCHARTS_MISSING_MARKER } from 'lumen/utils/constants/general-constants';
import {
  getDefaultChartOptions,
  getDefaultTooltipOptions,
} from 'lumen/utils/rates-chart';
import { timeout } from 'lumen/utils/waiters';

const NUM_Y_TICKS = 7;

// The margin of units (dollars, euros, ...) where we show two competitors as highlighted
const RATES_CHART_TOOLTIP_MARGIN = 4;

const SERIES = {
  DUMMY: 'dummy',
  OTB: 'otb',
  MARKET_DEMAND: 'market-demand',
  MEDIAN_COMP_RATE: 'median-comp-rate',
  RANGE: 'range',
  SHORT_TERM_RENTAL: 'short-term-rental',
};

/* This function is a snowflake implementation to convert hex color values to RGB.
  It is used to generate a gradient with varying transparency from a PRISM color token, to visualise the min-max range in the rates chart.
  It is not a general-purpose utility and should not be used elsewhere. */
// @ts-expect-error: Incorrect type
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const hexToRgb = (hex) => {
  let red = 255;
  let green = 152;
  let blue = 0;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  let cleanHex = hex.replace(/^#/, '');

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (cleanHex.length === 3) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    cleanHex = cleanHex
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .split('')
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      .map((c) => c + c)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .join('');
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  red = parseInt(cleanHex.slice(0, 2), 16);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  green = parseInt(cleanHex.slice(2, 4), 16);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  blue = parseInt(cleanHex.slice(4, 6), 16);

  return { red, green, blue };
};

interface HotelRatesChartsChartRatesSignature {
  Args: {
    competitorsForCurrentHotelAndOTA: unknown;
    isBestFlex: unknown;
    message: unknown;
    onUpdateChartReference: unknown;
    params: unknown;
    periods: unknown;
    ratesEvolutionOrBookingCurveEnabled: unknown;
    redirectToDayDetail: unknown;
    selectedOTA: unknown;
  };
}

export default class HotelRatesChartsChartRates extends Component<HotelRatesChartsChartRatesSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked tooltipDay = null;

  hiddenSeries = trackedSet([
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    ...this.args.competitorsForCurrentHotelAndOTA.map((c) => c.id),
  ]);
  tooltip = getDefaultTooltipOptions();

  trackDayHoverTask = task({ drop: true }, async () => {
    await timeout('extra-long');
    this.usageAnalytics.track('Daily Details Viewed', {
      eventTrigger: 'chart-view',
      view: 'chart',
      dateViewed: this.tooltipDay,
    });
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // We added this because the chartData needs to be regenerated,
    // if the day request ends later than the period request (rates route)
    // @ts-expect-error: Incorrect type
    if (this.currentHotel?.loading) {
      return undefined;
    }

    // Get default rates from own and compset hotels
    const { data, meanData } = this.ratesData;
    const [medianData, minmaxData, zonesMinMax] =
      this.addMinMaxAndMedian(meanData);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const demand = this.getDemandData();

    const chartData = [];

    chartData.push({
      data: minmaxData,
      id: SERIES.RANGE,
      marker: {
        enabled: false,
      },
      name: 'range',
      type: 'arearange',
      visible: !this.hiddenSeries.has(SERIES.MEDIAN_COMP_RATE),
      zoneAxis: 'x',
      zones: zonesMinMax,
    });

    chartData.push({
      color: 'var(--prism-color-chart-categorical-14)',
      data: medianData,
      id: SERIES.MEDIAN_COMP_RATE,
      marker: {
        enabled: true,
        radius: 4,
      },
      name: this.intl.t('rates.median_comp_rate'),
      type: 'line',
      visible: !this.hiddenSeries.has(SERIES.MEDIAN_COMP_RATE),
      zIndex: 20,
    });

    if (this.access.hasFeature('subscription.demand') && this.hasDemand) {
      chartData.push({
        color: 'var(--prism-color-general-neutral-600)',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data: demand,
        id: SERIES.MARKET_DEMAND,
        infoText: this.access.hasMarketInsight
          ? this.intl.t('market_demand.tooltip_di.info')
          : this.intl.t('market_demand.tooltip_ari.info'),
        name: this.intl.t('demand.market_demand'),
        type: 'column',
        visible: !this.hiddenSeries.has(SERIES.MARKET_DEMAND),
        yAxis: 1,
        zIndex: 10,
      });
    }

    // @ts-expect-error: Incorrect type
    return chartData.concat(data);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    const { ratesEvolutionOrBookingCurveEnabled, redirectToDayDetail } =
      this.args;

    const defaultOptions = getDefaultChartOptions(
      this.xAxis,
      this.yAxis,
      this.tooltip,
    );

    const plotSeries = {
      events: {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        click: (event) => {
          if (ratesEvolutionOrBookingCurveEnabled) {
            this.usageAnalytics.track('Detailed View Opened', {
              eventTrigger: 'chart-view',
            });

            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            redirectToDayDetail(dayjs.utc(event.point.x).format('YYYY-MM-DD'));
          }
        },
      },
    };

    return {
      ...defaultOptions,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      chart: {
        // @ts-expect-error: Incorrect type
        ...defaultOptions.chart,
        borderRadius: 3,
        marginLeft: 110,
        marginRight: 77.5,
      },
      legend: {
        enabled: false,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      plotOptions: {
        // @ts-expect-error: Incorrect type
        ...defaultOptions.plotOptions,
        series: plotSeries,
      },
      title: {
        text: 'rates-chart',
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartPlotBands() {
    const rates = this.args.periods ?? [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const startDate = rates[0]?.fromDate;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const endDate = rates[rates.length - 1]?.fromDate;

    return getWeekendPlotBands(
      this.hotelAndMarketService,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      startDate,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      endDate,
      'var(--prism-color-background-neutral-default)',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentHotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasDemand() {
    let hasDemand = false;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.periods.forEach((period) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (period.day?.demand?.message !== 'general.missing') {
        hasDemand = true;
      }
    }); // remove empty items from map;
    return hasDemand;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendData() {
    // ChartData is null when currentHotel.isHotelLoading is true
    return this.chartData?.filter((series) => {
      return !(series.id === SERIES.DUMMY || series.id === SERIES.RANGE);
    });
  }

  /**
   * Own + competitors' rates
   * @return {Object} Object containing the data which can be displayed in the chart + the collected mean data
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ratesData() {
    const { competitorsForCurrentHotelAndOTA } = this.args;
    const { yAxisMax } = this;

    const ownHotelAndCompetitors = [
      this.currentHotel?.hotelinfo,
      // @ts-expect-error: Incorrect type
      ...competitorsForCurrentHotelAndOTA,
    ];

    // Will be used to determine median and range series
    const meanData = new Array(ownHotelAndCompetitors.length);

    const data = ownHotelAndCompetitors.map((hotel) =>
      this.collectRatesForHotel(hotel, yAxisMax, meanData),
    );

    if (
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      // @ts-expect-error: Incorrect type
      data.push(this.collectShortTermRentalData());
    }

    return { data, meanData };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tickPositions() {
    const { competitorsForCurrentHotelAndOTA, periods } = this.args;

    return calculateTickPositions(
      this.currentHotel,
      competitorsForCurrentHotelAndOTA,
      periods,
      this.yAxisMax,
      this.yAxisMin,
      NUM_Y_TICKS,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get xAxis() {
    return {
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const date = dayjs.utc(this.value).format('ll');

          return date;
        },
        rotation: -45,
      },
      plotBands: this.chartPlotBands,
      type: 'datetime',
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxis() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const yAxisArray = [
      {
        // Add a margin, if the largest point is equal to yAxisMax the point woint be visible
        ceiling: this.yAxisMax + 5,
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            const label = that.hotelAndMarketService.formatRate(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              true,
              false,
            );

            return label === null ? label : `${label}`;
          },
        },
        showFirstLabel: false,
        tickAmount: NUM_Y_TICKS,
        tickPositions: this.tickPositions,
        title: {
          text: '',
        },
      },
      {
        ceiling: 100,
        labels: {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            // @ts-expect-error: Incorrect type
            return `${this.value}%`;
          },
        },
        min: 0,
        opposite: true,
        showFirstLabel: false,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          const positions = [];

          // 2 because we show one tick above max and one tick below min respectively
          let increment = Math.ceil(
            // @ts-expect-error: Incorrect type
            (this.dataMax - this.dataMin) / (NUM_Y_TICKS - 2),
          );
          // Multiple of 5
          increment = 5 * Math.ceil(increment / 5);

          // @ts-expect-error: Incorrect type
          let tick = 5 * Math.floor(this.dataMin / 5);
          tick -= increment;

          // @ts-expect-error: Incorrect type
          if (this.dataMax) {
            // 7 ticks are required
            for (let i = 0; i < NUM_Y_TICKS; i++) {
              positions.push(tick);
              tick += increment;
            }
          }

          return positions;
        },
        title: {
          text: this.intl.t('demand.market_demand'),
        },
      },
    ];

    if (this.access.hasFeature('subscription.demand') && this.hasDemand) {
      return yAxisArray;
    }

    return yAxisArray[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @cached get yAxisMax() {
    const { competitorsForCurrentHotelAndOTA, periods } = this.args;

    const yAxisMax = calculateYaxisMax(
      this.currentHotel,
      competitorsForCurrentHotelAndOTA,
      periods,
    );

    if (
      // @ts-expect-error: Incorrect type
      periods.length &&
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const rentalRateValues = periods.map((period) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return period.day.rentalRates[0]?.value ?? Number.MIN_VALUE;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return Math.max(yAxisMax, ...rentalRateValues);
    }

    return yAxisMax;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisMin() {
    const { periods } = this.args;

    if (
      // @ts-expect-error: Incorrect type
      periods.length &&
      this.access.hasShortTermRentalAccess &&
      !this.access.hasShortTermRentalOnlyStrTab
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const rentalRateValues = periods.map((period) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return period.day.rentalRates[0]?.value ?? Number.MAX_VALUE;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return Math.min(...rentalRateValues);
    }

    return null; // Don't overwrite yAxis min by default
  }

  // @ts-expect-error: Incorrect type
  constructor(owner, args: HotelRatesChartsChartRatesSignature['Args']) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    super(owner, args);

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    set(this, 'tooltip.formatter', function () {
      /*
        The formatter will set the necessary value needed for the tooltip component.
        The highcharts formatter requires html to be returned (in this case)
        so we need to get the rendered html from the template
        The tooltip component is placed in a dummy div where this html can be obtained.
        Caution: the data for the component needs to be set in a different run loop,
        otherwise stale data will be returned
      */
      // http://stackoverflow.com/questions/7557533/display-tooltip-for-invisible-series-in-highcharts
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const { chart } = this.series;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { series } = chart;

      let index = 0;

      // compute the index of corr y value in each data arrays
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      const firstSeriesWithData = series.find(({ data }) => data.length);

      while (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        firstSeriesWithData?.data[index] &&
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.x !== firstSeriesWithData.data[index].x
      ) {
        index++;
      }

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const yValue = this.y;
      // @ts-expect-error: Incorrect type
      const hoveredHotels = [];

      // Necessary to get correct data for tooltips, needs to be in separate runloop
      // eslint-disable-next-line ember/no-runloop
      begin();
      // @ts-expect-error: Incorrect type
      set(that, 'period', that.args.periods[index]);
      set(that, 'series', series);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      set(that, 'tooltipDay', that.args.periods[index].day.formattedDate);

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      series.forEach(({ data }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (data.length === 0) {
          return;
        }

        // Give some margin for rates that are practically the same
        // Some Y values may be null (sold out,...); check is needed because of that
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          data[index]?.y &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          Math.abs(data[index].y - yValue) < RATES_CHART_TOOLTIP_MARGIN
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          hoveredHotels.push(data[index].hotelId);
        }
      });

      // @ts-expect-error: Incorrect type
      set(that, 'hoveredHotels', hoveredHotels);
      set(that, 'median', that.getMedianCompRate(index));
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      that.trackDayHoverTask.perform();
      // eslint-disable-next-line ember/no-runloop
      end();

      // @ts-expect-error: Incorrect type
      return document.getElementById('dummy-div-for-tooltip').innerHTML;
    });
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  addMinMaxAndMedian(data) {
    // @ts-expect-error: Incorrect type
    const resMedian = [];
    // @ts-expect-error: Incorrect type
    const resMinMax = [];
    // @ts-expect-error: Incorrect type
    const zonesMinMax = [];

    const { red, green, blue } = hexToRgb(
      getCssVariableValue('--prism-color-chart-status-figure-warning-normal'),
    );

    // Start with opacity zero
    // (will not be visible anyway, since start of first one is not visible on graph)
    let previousColor = `rgba(${red},${green},${blue},0)`;
    // @ts-expect-error: Incorrect type
    let previousRate = null;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    data.forEach((datum, index) => {
      // Get your own hotel
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const ownHotelPeriods = datum.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        ({ isCurrentHotel }) => isCurrentHotel,
      );

      // Get all other hotels, on the condition that they are not sold out, LOS2,...
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const sortedPeriods = datum
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .filter(({ isCurrentHotel, tooltipValue, y }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return !isCurrentHotel && y && tooltipValue && tooltipValue !== 0;
        })
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort((a, b) => a.tooltipValue - b.tooltipValue);

      const maxOpacity = 0.5;
      const opacityFactor = 0.4;

      const opacity =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        maxOpacity - opacityFactor * (1 - sortedPeriods.length / datum.length);
      const color = `rgba(${red},${green},${blue},${opacity})`;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      addZone(zonesMinMax, datum[0].x, {
        color: {
          linearGradient: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            x1: (index - 1) / (data.length - 1),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            x2: index / (data.length - 1),
            y1: 0,
            y2: 0,
          },
          stops: [
            [0, previousColor],
            [0.25, previousColor],
            [0.75, color],
            [1, color],
          ],
        },
      });

      previousColor = color;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isRateMissing = sortedPeriods.length === 0;

      // If this filter gives an empty result, let the median and range converge to the own hotel
      if (isRateMissing) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const { x, y } = ownHotelPeriods[0];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const yNormalized = y ?? previousRate;

        resMinMax.push({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          high: yNormalized,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          low: yNormalized,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          x,
        });
        resMedian.push({
          marker: HIGHCHARTS_MISSING_MARKER,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          x,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: yNormalized,
        });
      } else {
        // Push this data to the range graph
        resMinMax.push(this.minMax(sortedPeriods));
        // Use the sorted period to calculate the median
        resMedian.push(this.findMedian(sortedPeriods));

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        previousRate = sortedPeriods[0].y;
      }
    });

    // @ts-expect-error: Incorrect type
    return [resMedian, resMinMax, zonesMinMax];
  }

  /**
   * Gets all periods for a given hotel and maps them to an array of points
   * These points can be used directly in a rates-chart
   * @param  {Object}  hotel    The hotel object for which to collect points
   * @param  {Number}  yAxisMax The current max value on the y-axis
   * @param  {Object}  meanData A collection used to keep track of mean data
   * @return {Array}            Returns an array containing the datapoints for the chart
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  collectRatesForHotel(hotel, yAxisMax, meanData) {
    const { periods } = this.args;

    // Determine points if previous is Sold out, LOS2,...
    let previousValueX = 0;
    // @ts-expect-error: Incorrect type
    let previousValueY = null;
    // @ts-expect-error: Incorrect type
    const zones = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isCurrentHotel = hotel.id === this.currentHotel?.hotelinfo?.id;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const hotelId = hotel.id;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const current = periods
      // @ts-expect-error: Incorrect type
      .map((period, index) => {
        // Rates for current hotel
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const compRates = get(period, `rates.${hotel.id}`);

        if (compRates !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const selectedRate = compRates[0];
          const isSoldOut =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            !selectedRate || !selectedRate.value || selectedRate.value === 0;

          const xValue = this.xValue(period);
          const xSoldOut = this.xSoldOut(period);

          let dataPoint;

          if (!isSoldOut) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            [dataPoint, previousValueX, previousValueY] = connectRateDataPoint(
              this.hotelAndMarketService,
              xValue,
              xSoldOut,
              // @ts-expect-error: Incorrect type
              zones,
              selectedRate,
              yAxisMax,
              hotelId,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            dataPoint = {
              ...dataPoint,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              membershiptype: selectedRate.membershiptype,
            };

            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            collectMeanData(meanData, dataPoint, isCurrentHotel, index);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return dataPoint;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          [dataPoint, previousValueX] = connectUndefinedRateDataPoint(
            previousValueX,
            // @ts-expect-error: Incorrect type
            previousValueY,
            // @ts-expect-error: Incorrect type
            zones,
            xValue,
            xSoldOut,
            index,
            selectedRate,
            hotelId,
          );

          // Collect the mean data for later median calculus
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          collectMeanData(meanData, dataPoint, isCurrentHotel, index);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return dataPoint;
        }
        return undefined;
      })
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .filter(Boolean);

    const hotelSeriesConfig = getSeriesConfiguration(
      hotel,
      current,
      isCurrentHotel,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.activeCompset?.competitors,
      // @ts-expect-error: Incorrect type
      zones,
    );

    const series = {
      ...hotelSeriesConfig,
      // make sure the hotel is only visible if it is the currenthotel,
      // or if this is the rate evolution chart, in which case the hotels are always visible
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      visible: !that.hiddenSeries.has(hotel.id),
    };

    return series;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  collectShortTermRentalData() {
    const { periods } = this.args;

    const series = {
      color: SHORT_TERM_RENTAL_RATE_COLOR,
      data: [],
      id: SERIES.SHORT_TERM_RENTAL,
      name: this.intl.t('short_term_rental.rate'),
      type: 'line',
      visible: !this.hiddenSeries.has(SERIES.SHORT_TERM_RENTAL),
      zIndex: 20,
    };

    // @ts-expect-error: Incorrect type
    let previousValueY = null;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    periods.forEach((period) => {
      // In the Rates Chart view we can go via the Period model
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rentalRateForExtractDate = period.day.rentalRates?.[0];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { isMissing } = rentalRateForExtractDate;
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const y = isMissing ? previousValueY : rentalRateForExtractDate.value;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      previousValueY = isMissing
        ? // @ts-expect-error: Incorrect type
          previousValueY
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          rentalRateForExtractDate.value;

      const seriesData = {
        hotelId: SERIES.SHORT_TERM_RENTAL,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        tooltipValue: Math.round(rentalRateForExtractDate.value),
        x: this.xValue(period),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y,
      };

      if (isMissing) {
        // @ts-expect-error: Incorrect type
        seriesData.marker = HIGHCHARTS_MISSING_MARKER;
      }

      // @ts-expect-error: Incorrect type
      series.data.push(seriesData);
    });

    return series;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findMedian(sortedPeriods) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const mid = Math.floor(sortedPeriods.length / 2);
    let median;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (sortedPeriods.length % 2 === 0) {
      const tooltipValue =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (sortedPeriods[mid].tooltipValue +
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          sortedPeriods[mid - 1].tooltipValue) /
        2;

      median = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        isCurrentHotel: sortedPeriods[mid].isCurrentHotel,
        tooltipValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        x: sortedPeriods[mid].x,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        y: (sortedPeriods[mid].y + sortedPeriods[mid - 1].y) / 2,
      };
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      median = sortedPeriods[mid];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const isOutlier = median.tooltipValue > this.yAxisMax;

    if (isOutlier) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      median.dataLabels = {
        enabled: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          const label = that.hotelAndMarketService.formatRate(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            this.point.tooltipValue,
            false,
            false,
          );

          return label === null ? label : `${label}`;
        },
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return median;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getDemandData() {
    const { periods } = this.args;

    if (this.access.hasFeature('subscription.demand') && this.hasDemand) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        periods
          // @ts-expect-error: Incorrect type
          .map((period) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (period.fromDate) {
              const xValue = this.xValue(period);

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              let currentDemand = period.day?.demand?.value * 100;
              // @ts-expect-error: Incorrect type
              currentDemand = currentDemand > 0 ? currentDemand : undefined;

              return [xValue, currentDemand];
            }

            return undefined;
          })
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter(Boolean)
      );
    }

    return null;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMedianCompRate(index) {
    const [medianData] = this.addMinMaxAndMedian(this.ratesData.meanData);

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return medianData[index].tooltipValue;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  minMax(sortedPeriods) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const total = sortedPeriods.reduce(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      (accumulator, { tooltipValue }) => accumulator + tooltipValue,
      0,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const average = total / sortedPeriods.length;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const summedVariance = sortedPeriods
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .map(({ tooltipValue }) => Math.pow(tooltipValue - average, 2))
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      .reduce((a, b) => a + b, 0);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const standardDeviation = Math.sqrt(summedVariance / sortedPeriods.length);

    return {
      high: Math.min(average + standardDeviation, this.yAxisMax),
      low: Math.max(average - standardDeviation, 0),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      x: sortedPeriods[0].x,
    };
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xSoldOut(period) {
    return this.hotelAndMarketService.toLocalUnixTime(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      period.fromDate.unix() + 24 * 60 * 60,
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(period) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    return this.hotelAndMarketService.toLocalUnixTime(period.fromDate.unix());
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  @action isSeriesHidden(series) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.hiddenSeries.has(series.id ?? series.hotelId);
  }

  // @ts-expect-error: Incorrect type
  @action toggleEntireCompset(isToggledOn): void {
    const { competitorsForCurrentHotelAndOTA } = this.args;

    if (isToggledOn) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      competitorsForCurrentHotelAndOTA.forEach((competitor) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.hiddenSeries.delete(competitor.id);
      });

      return;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    competitorsForCurrentHotelAndOTA.forEach((competitor) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.hiddenSeries.add(competitor.id);
    });
  }

  // @ts-expect-error: Incorrect type
  @action toggleSeries(legendItem): void {
    // id is undefined for own hotel, competitors and short-term rental rate (see rates-chart-utils.js)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const legendItemId = legendItem.id ?? legendItem.hotelId;
    const wasHidden = this.hiddenSeries.has(legendItemId);

    if (wasHidden) {
      this.hiddenSeries.delete(legendItemId);
    } else {
      this.hiddenSeries.add(legendItemId);
    }

    if (legendItemId === SERIES.MEDIAN_COMP_RATE) {
      // Show all competitors if the median is toggled off (and vice versa)
      this.toggleEntireCompset(this.hiddenSeries.has(SERIES.MEDIAN_COMP_RATE));
    }

    if (legendItemId === SERIES.OTB) {
      // Show all competitors if OTB is toggled off (and vice versa)
      this.toggleEntireCompset(this.hiddenSeries.has(SERIES.OTB));
    }
  }

  <template>
    <GeneralChartsOtaChart
      @chartOptions={{this.chartOptions}}
      {{! @glint-expect-error: Incorrect type }}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />

    <div class="chart-legend mt-400 flex-wrap gap-200">
      {{#each this.legendData as |legendItem|}}
        {{! @glint-expect-error: Incorrect type }}
        <GeneralChartsLegendsLegendItem
          @color={{legendItem.color}}
          @isHidden={{this.isSeriesHidden legendItem}}
          @legendItemClicked={{fn this.toggleSeries legendItem}}
          @name={{legendItem.name}}
          @tooltipContent={{legendItem.infoText}}
          @type={{legendItem.type}}
        >
          {{legendItem.name}}
        </GeneralChartsLegendsLegendItem>
      {{/each}}
    </div>

    {{! @glint-expect-error: Incorrect type }}
    {{#if @message.hasMessage}}
      <div class="message-overlay">
        <div class="message-wrapper">
          {{! @glint-expect-error: Incorrect type }}
          {{@message.message}}
        </div>
      </div>
    {{/if}}

    <div class="hidden" id="dummy-div-for-tooltip">
      <div class="rates-calendar-tooltip">
        <GeneralTooltipsTooltipRatesCalendar
          {{! @glint-expect-error: Incorrect type }}
          @competitorsForCurrentHotelAndOTA={{@competitorsForCurrentHotelAndOTA}}
          {{! @glint-expect-error: Incorrect type }}
          @hoveredHotels={{this.hoveredHotels}}
          @isBestFlex={{@isBestFlex}}
          {{! @glint-expect-error: Incorrect type }}
          @median={{this.median}}
          {{! @glint-expect-error: Incorrect type }}
          @otaId={{@selectedOTA.id}}
          {{! @glint-expect-error: Incorrect type }}
          @params={{@params}}
          {{! @glint-expect-error: Incorrect type }}
          @period={{this.period}}
          {{! @glint-expect-error: Incorrect type }}
          @series={{this.series}}
        />
      </div>
    </div>
  </template>
}
